import {
  AmsReconciliationLineOfBusiness,
  type AmsReconciliationTargetFieldsFragment
} from '@oysterjs/ais/api/graphql';

type Target = AmsReconciliationTargetFieldsFragment;
type PolicyGroup = NonNullable<Target['policyGroup']>;

/** One policy on the account, with the destinations nested under it. */
export type DestinationPolicy = {
  group: PolicyGroup;
  targets: Target[];
  suggested: boolean;
};

export type DestinationLayout = {
  suggestedPolicies: DestinationPolicy[];
  otherPolicies: DestinationPolicy[];
  /** A detected destination the account has no policy for. */
  suggestedLoose: Target[];
};

/**
 * Nests an account's destinations under their policies, keeping the server's order.
 * A policy counts as suggested when detection landed on any of its destinations, so
 * the policy the document is about is the one step one opens.
 */
export function layOutDestinations(targets: Target[]): DestinationLayout {
  const policies = new Map<string, DestinationPolicy>();
  const suggestedLoose: Target[] = [];

  for (const target of targets) {
    if (!target.policyGroup) {
      // A record on no policy that the document did not ask for, such as a
      // certificate, cannot be written and is not offered.
      if (target.detectedFromDocument) {
        suggestedLoose.push(target);
      }
      continue;
    }
    let policy = policies.get(target.policyGroup.id);
    if (!policy) {
      policy = { group: target.policyGroup, targets: [], suggested: false };
      policies.set(target.policyGroup.id, policy);
    }
    policy.targets.push(target);
    policy.suggested = policy.suggested || target.detectedFromDocument;
  }

  // The application leads because every line on the policy shares it.
  const all = [...policies.values()].map((policy) => ({
    ...policy,
    targets: [...policy.targets].sort(
      (left, right) => Number(!isApplicant(left)) - Number(!isApplicant(right))
    )
  }));

  return {
    suggestedPolicies: all.filter((policy) => policy.suggested),
    otherPolicies: all.filter((policy) => !policy.suggested),
    suggestedLoose
  };
}

/** Targets in the order the picker draws them, which the review and its writes follow. */
export function inPickerOrder(targets: Target[]): Target[] {
  const layout = layOutDestinations(targets);
  return [
    ...layout.suggestedPolicies.flatMap((policy) => policy.targets),
    ...layout.suggestedLoose,
    ...layout.otherPolicies.flatMap((policy) => policy.targets)
  ];
}

/**
 * Names a destination outside the picker, where no policy heading sits above it: two
 * policies each have an "Applicant", and only the policy tells them apart.
 */
export function destinationName(target: Pick<Target, 'label' | 'policyGroup'>): string {
  return target.policyGroup ? `${target.policyGroup.label} · ${target.label}` : target.label;
}

/**
 * One line per policy, naming it once before its destinations, in the order given. A
 * destination on no policy has nothing to share a line with, so it takes its own.
 */
export function destinationLines(targets: Pick<Target, 'label' | 'policyGroup'>[]): string[] {
  const lines: { policy?: string; labels: string[] }[] = [];
  const byPolicy = new Map<string, { policy?: string; labels: string[] }>();
  for (const target of targets) {
    if (!target.policyGroup) {
      lines.push({ labels: [target.label] });
      continue;
    }
    let line = byPolicy.get(target.policyGroup.id);
    if (!line) {
      line = { policy: target.policyGroup.label, labels: [] };
      byPolicy.set(target.policyGroup.id, line);
      lines.push(line);
    }
    line.labels.push(target.label);
  }
  return lines.map((line) =>
    line.policy ? `${line.policy}: ${line.labels.join(', ')}` : line.labels.join(', ')
  );
}

function isApplicant(target: Target): boolean {
  return target.lineOfBusiness === AmsReconciliationLineOfBusiness.Applicant;
}
